import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Crown, Bell, Loader, RefreshCw } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stripe lookup keys (created in catalog setup)
const STRIPE_KEYS = {
  pro_monthly: "pro_monthly",
  pro_annual: "pro_annual",
  elite_monthly: "elite_monthly",
  elite_annual: "elite_annual",
};

const CANCEL_REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_enough_value", label: "Not enough value" },
  { value: "found_alternative", label: "Found an alternative" },
  { value: "too_busy", label: "Too busy to use it" },
  { value: "other", label: "Other" },
];

const plans = [
  {
    id: "free",
    name: "Free",
    monthlyPrice: "$0",
    annualPrice: "$0",
    period: "forever",
    description: "Get started, find partners, browse venues.",
    cta: "Get Started Free",
    ctaLink: "/register",
    highlight: false,
    isSubscription: false,
    features: [
      "Player profile with 2 sports",
      "Partner search & browse",
      "Up to 5 play invites/month",
      "Venue map browsing",
      "Basic messaging (10/month)",
      "Session history",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    monthlyPrice: "$12",
    annualPrice: "$9.60",
    annualTotal: "$115",
    period: "per month",
    description: "Serious players who want every advantage.",
    cta: "Subscribe Now",
    highlight: true,
    badge: "Most Popular",
    isSubscription: true,
    monthlyKey: STRIPE_KEYS.pro_monthly,
    annualKey: STRIPE_KEYS.pro_annual,
    features: [
      "All Free features",
      "Unlimited sports profiles",
      "Unlimited play invites",
      "Travel mode (any city)",
      "Advanced filters (schedule, format)",
      "AI match explanations",
      "Unlimited messaging",
      "Profile visibility boost",
      "Read receipts",
      "Safety controls & preferences",
    ],
  },
  {
    id: "elite",
    name: "Elite",
    monthlyPrice: "$29",
    annualPrice: "$23.20",
    annualTotal: "$278",
    period: "per month",
    description: "For dedicated athletes and frequent travelers.",
    cta: "Go Elite",
    highlight: false,
    isSubscription: true,
    monthlyKey: STRIPE_KEYS.elite_monthly,
    annualKey: STRIPE_KEYS.elite_annual,
    features: [
      "All Pro features",
      "AI Concierge matching service",
      "Destination sport packs (ski, golf)",
      "Verified skill assessment",
      "Priority support",
      "First access to new features",
      "Annual activity recap",
      "Ambassador discount code",
    ],
  },
];

const clubPlan = {
  name: "Club & Facility",
  price: "From $99",
  period: "per location/month",
  description: "Tools built for club operators and facility managers.",
  features: [
    "Member management & billing",
    "Court / table reservations",
    "Digital waivers & check-in",
    "Email & SMS campaigns",
    "Event signups & payments",
    "Staff roles & permissions",
    "Analytics & attendance tracking",
    "Local SEO landing page",
    "Guest fee collection",
    "Promo codes & waitlists",
  ],
};

export default function PricingPage() {
  const { user } = useAuth();
  const showToast = useToast();
  const { features } = useFeatureFlags();
  const [annual, setAnnual] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelFeedback, setCancelFeedback] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const handleSubscribe = async (plan) => {
    if (!user) { window.location.href = "/register"; return; }
    // Guard in the handler as well as the button. The flag reflects the server's
    // view, which can change under us; failing here beats a 500 mid-click.
    if (!features.payments) {
      showToast("Paid plans are not available yet. We'll let you know when they are.");
      return;
    }
    const lookupKey = annual ? plan.annualKey : plan.monthlyKey;
    setCheckingOut(plan.id);
    try {
      const r = await axios.post(
        `${API}/payments/checkout`,
        { lookup_key: lookupKey, quantity: 1, origin_url: window.location.origin, user_id: user.id },
        { withCredentials: true }
      );
      window.location.href = r.data.checkout_url;
    } catch (err) {
      showToast(err?.response?.data?.detail || "Failed to start checkout. Please try again.");
    } finally {
      setCheckingOut(null);
    }
  };

  const handlePlusWaitlist = async () => {
    if (!user) { window.location.href = "/register"; return; }
    setJoiningWaitlist(true);
    try {
      await axios.post(`${API}/premium/waitlist`, { tier: "plus", feature_interest: "advanced_filters" }, { withCredentials: true });
      setWaitlistJoined(true);
      showToast("You're on the Plus waitlist! We'll reach out soon.");
    } catch {
      showToast("Failed to join waitlist. Please try again.");
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleCancelPlus = async () => {
    if (!cancelReason) return;
    setCancelling(true);
    try {
      await axios.patch(
        `${API}/users/me/plus/cancel`,
        null,
        { params: { reason: cancelReason, feedback: cancelFeedback }, withCredentials: true }
      );
      showToast("Your subscription has been cancelled.");
      setShowCancelModal(false);
    } catch {
      showToast("Failed to cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const isSubscribed = user?.subscription && user.subscription !== "free";

  return (
    /* dark bg ensures all text-white, text-gray-* pass WCAG AA */
    <div className="min-h-screen bg-[#0F1629] pt-16 dark-section">

      {/* Header */}
      <div className="py-16 px-4 text-center">
        {/* text-brand-green on #0F1629 → 6.5:1 ✓ */}
        <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">Pricing</p>
        {/* text-white on #0F1629 → ~14:1 ✓ */}
        <h1 className="text-4xl sm:text-5xl font-heading font-black text-white mb-4">Play More, Pay Less</h1>
        {/* text-gray-300 on #0F1629 → ~8:1 ✓ */}
        <p className="text-gray-300 max-w-lg mx-auto mb-8">
          Start free forever. Upgrade when you want unlimited matches, travel mode, and AI-powered recommendations.
        </p>

        {/* Annual / Monthly toggle */}
        <div className="inline-flex items-center gap-3 bg-[#161B28] border border-white/10 rounded-xl px-2 py-1.5 dark-section" role="group" aria-label="Billing period">
          <button
            onClick={() => setAnnual(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!annual ? "bg-brand-green text-white" : "bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white"}`}
            aria-pressed={!annual}
            data-testid="monthly-toggle"
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${annual ? "bg-brand-green text-brand-navy" : "text-gray-400 hover:text-white"}`}
            aria-pressed={annual}
            data-testid="annual-toggle"
          >
            Annual
            <span className="text-xs px-1.5 py-0.5 bg-brand-orange/20 text-brand-orange rounded font-bold">–20%</span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-16">
        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="pricing-plans">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`bg-[#161B28] border rounded-2xl overflow-hidden flex flex-col transition-all ${
                plan.highlight
                  ? "border-brand-green/50 shadow-[0_0_30px_rgba(82,227,145,0.08)] scale-[1.02] relative"
                  : "border-white/10 hover:border-brand-green/25"
              } dark-section`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <div className="bg-brand-orange text-white text-xs font-black text-center py-1.5 tracking-widest uppercase">
                  {plan.badge}
                </div>
              )}
              <div className="p-6 flex-1">
                <h2 className="text-xl font-heading font-bold text-white mb-1">{plan.name}</h2>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-heading font-black text-brand-green">
                    {annual && plan.annualPrice ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-gray-400 text-sm">/ {plan.period}</span>
                </div>
                {annual && plan.annualTotal && (
                  <p className="text-xs text-gray-500 mb-1">Billed {plan.annualTotal}/year</p>
                )}
                <p className="text-sm text-gray-400 mb-5">{plan.description}</p>

                <ul className="space-y-2.5 mb-6" aria-label={`${plan.name} features`}>
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={14} className="text-brand-green mt-0.5 flex-shrink-0" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 pt-0">
                {plan.isSubscription ? (
                  <button
                    onClick={() => handleSubscribe(plan)}
                    disabled={checkingOut === plan.id || isSubscribed || !features.payments}
                    className={`block w-full text-center py-3 rounded-xl font-bold text-sm transition-colors ${
                      plan.highlight
                        ? "bg-brand-orange text-white hover:bg-brand-orange/90"
                        : "bg-white/8 border border-white/15 text-white hover:bg-white/12 hover:border-brand-green/40"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                    aria-label={`Subscribe to ${plan.name} plan`}
                  >
                    {checkingOut === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader size={14} className="animate-spin" /> Redirecting…
                      </span>
                    ) : isSubscribed && user?.subscription === plan.id.toLowerCase() ? (
                      "Current Plan"
                    ) : !features.payments ? (
                      // Say why it is unavailable rather than presenting a dead
                      // button. A disabled control with no explanation reads as
                      // a broken page.
                      "Coming soon"
                    ) : (
                      plan.cta
                    )}
                  </button>
                ) : (
                  <Link
                    to={user ? "/find-partners" : "/register"}
                    className="block w-full text-center py-3 rounded-xl font-bold text-sm border border-white/15 text-gray-300 hover:border-brand-green/30 hover:text-brand-green transition-colors"
                    data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Cancel subscription link */}
        {isSubscribed && (
          <div className="text-center mb-8">
            <button
              onClick={() => setShowCancelModal(true)}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors underline-offset-2 hover:underline"
              data-testid="cancel-subscription-btn"
            >
              Cancel subscription
            </button>
          </div>
        )}

        {/* Club plan */}
        <div id="clubs" className="bg-[#161B28] border border-white/10 rounded-2xl p-8 hover:border-brand-green/20 transition-colors dark-section" data-testid="club-pricing">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-2">B2B</p>
              <h2 className="text-3xl font-heading font-bold text-white mb-2">{clubPlan.name}</h2>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-3xl font-heading font-black text-brand-green">{clubPlan.price}</span>
                <span className="text-gray-400 text-sm">/ {clubPlan.period}</span>
              </div>
              <p className="text-gray-400 text-sm mb-5">{clubPlan.description}</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange/90 transition-colors"
                data-testid="club-cta"
              >
                Schedule a Demo <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-2" aria-label="Facility plan features">
              {clubPlan.features.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-gray-300">
                  <Check size={13} className="text-brand-green flex-shrink-0" aria-hidden="true" /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Plus waitlist banner */}
        <div className="mt-10 bg-[#161B28] border border-brand-green/25 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6 dark-section" data-testid="plus-waitlist-section">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-brand-green/15 border border-brand-green/30 flex items-center justify-center" aria-hidden="true">
            <Crown size={22} className="text-brand-green" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="text-xs text-brand-green uppercase tracking-widest font-medium mb-1">Early Access</p>
            <h3 className="text-xl font-heading font-bold text-white mb-1">ActivitySports Plus</h3>
            <p className="text-gray-400 text-sm">Exclusive concierge matching, verified partner badges, travel mode, and priority support. Join the waitlist to get early access.</p>
          </div>
          <button
            onClick={handlePlusWaitlist}
            disabled={joiningWaitlist || waitlistJoined}
            className={`flex items-center gap-2 px-6 py-3 font-bold rounded-xl transition-colors whitespace-nowrap ${
              waitlistJoined ? "bg-brand-green/10 text-brand-green border border-brand-green/30" : "bg-brand-green text-brand-navy hover:bg-brand-green/90"
            }`}
            data-testid="join-plus-waitlist-btn"
            aria-label={waitlistJoined ? "On Plus waitlist" : "Join Plus waitlist"}
          >
            {waitlistJoined ? <><Check size={15} aria-hidden="true" /> On Waitlist!</> : <><Bell size={15} aria-hidden="true" /> {joiningWaitlist ? "Joining…" : "Join Waitlist"}</>}
          </button>
        </div>

        {/* FAQ */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-heading font-bold text-white mb-6">Common Questions</h3>
          <div className="grid md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
            {[
              { q: "Is the free plan really free forever?", a: "Yes. No credit card required. Browse players, venues, and send up to 5 invites per month at no cost." },
              { q: "Can I cancel my subscription anytime?", a: "Absolutely. Cancel at any time and retain access until the end of your billing period." },
              { q: "How does travel mode work?", a: "Set a destination city and travel dates. You'll appear in that city's search results for the duration of your trip." },
              { q: "What payment methods do you accept?", a: "We accept all major credit/debit cards via Stripe. Subscriptions are billed monthly or annually (20% discount)." },
            ].map(({ q, a }) => (
              <div key={q} className="bg-[#161B28] border border-white/8 rounded-xl p-4 dark-section">
                <h4 className="text-sm font-bold text-white mb-2">{q}</h4>
                <p className="text-sm text-gray-400">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cancel subscription modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Cancel subscription">
          <div className="bg-[#161B28] border border-white/15 rounded-2xl w-full max-w-md p-6 dark-section">
            <h2 className="text-lg font-bold text-white mb-1">Cancel Subscription</h2>
            <p className="text-gray-400 text-sm mb-5">We're sorry to see you go. Help us improve by sharing why.</p>
            <div className="space-y-2 mb-4">
              {CANCEL_REASONS.map((r) => (
                <label key={r.value} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="cancel_reason"
                    value={r.value}
                    checked={cancelReason === r.value}
                    onChange={() => setCancelReason(r.value)}
                    className="accent-brand-green"
                    data-testid={`cancel-reason-${r.value}`}
                  />
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{r.label}</span>
                </label>
              ))}
            </div>
            <textarea
              value={cancelFeedback}
              onChange={e => setCancelFeedback(e.target.value)}
              placeholder="Any additional feedback? (optional)"
              rows={2}
              className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 resize-none mb-5 dark-section"
              data-testid="cancel-feedback-input"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowCancelModal(false)} className="flex-1 py-2.5 text-sm border border-white/10 text-gray-400 rounded-xl hover:border-white/20 transition-colors">Keep Plan</button>
              <button
                onClick={handleCancelPlus}
                disabled={!cancelReason || cancelling}
                className="flex-1 py-2.5 text-sm font-bold bg-red-500/20 border border-red-500/40 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors disabled:opacity-50"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? <span className="flex items-center justify-center gap-2"><Loader size={14} className="animate-spin" />Cancelling…</span> : "Cancel Subscription"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
